// gym/GymMemberships.jsx (version avec gestion multi-devises)
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Alert, Snackbar, Chip, 
  FormControl, InputLabel, Select, MenuItem,
  Tooltip, Card, CardContent, CircularProgress,
  InputAdornment, ToggleButtonGroup, ToggleButton,
  Badge
} from '@mui/material';
import { 
  Add, Edit, Delete, Search, Refresh, CalendarToday,
  Person, AttachMoney, Receipt, TrendingUp,
  CurrencyExchange, CheckCircle, Cancel,
  AccessTime, TimerOff, CreditCard, Euro
} from '@mui/icons-material';
import api from '../../src/services/auth';
import { format, isBefore, parseISO, isValid, addMonths, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';

const GymMemberships = () => {
  const [memberships, setMemberships] = useState([]);
  const [clients, setClients] = useState([]);
  const [gymSettings, setGymSettings] = useState(null);
  const [filteredMemberships, setFilteredMemberships] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMembership, setEditingMembership] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currencyView, setCurrencyView] = useState('both'); // 'htg', 'usd', 'both'
  const [formData, setFormData] = useState({
    client: '',
    membership_type: 'MONTHLY',
    start_date: format(new Date(), 'yyyy-MM-dd'),
    duration_months: 1,
    price_htg: '',
    price_usd: '',
    status: 'ACTIVE',
    notes: '',
    end_date: ''
  });
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Types d'abonnements prédéfinis avec prix en USD
  const membershipTypes = [
    { value: 'MONTHLY', label: 'Mensuel', base_price_usd: 30 },
    { value: 'DAILY', label: 'Journalier', base_price_usd: 5 },
  ];

  // Statuts valides selon votre API Django
  const statusOptions = [
    { value: 'ACTIVE', label: 'Actif', color: 'success' },
    { value: 'EXPIRED', label: 'Expiré', color: 'warning' },
    { value: 'CANCELLED', label: 'Annulé', color: 'error' },
    { value: 'SUSPENDED', label: 'Suspendu', color: 'secondary' }
  ];

  // Méthodes de paiement
  const paymentMethods = [
    { value: 'CASH', label: 'Cash', icon: <AttachMoney /> },
    { value: 'CARD', label: 'Carte', icon: <CreditCard /> },
    { value: 'MONCASH', label: 'MonCash', icon: <CurrencyExchange /> },
    { value: 'TRANSFER', label: 'Virement', icon: <Receipt /> }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterMemberships();
  }, [memberships, searchTerm, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchGymSettings(), fetchClients(), fetchMemberships()]);
    } catch (error) {
      console.error('Error fetching data:', error);
      showError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchGymSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await api.get('/gym/settings/');
      let settingsData;
      
      if (Array.isArray(response.data)) {
        settingsData = response.data[0];
      } else if (response.data.results) {
        settingsData = response.data.results[0];
      } else {
        settingsData = response.data;
      }
      
      setGymSettings(settingsData);
      console.log('Gym settings loaded for memberships:', settingsData);
    } catch (error) {
      console.error('Error fetching gym settings:', error);
    } finally {
      setLoadingSettings(false);
    }
  };

  const fetchMemberships = async () => {
    try {
      console.log('Fetching memberships...');
      const response = await api.get('/gym/memberships/');
      
      let membershipsData = [];
      if (Array.isArray(response.data)) {
        membershipsData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        membershipsData = response.data.results;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        membershipsData = response.data.data;
      }
      
      console.log('Processed memberships data:', membershipsData);
      setMemberships(membershipsData);
      
    } catch (error) {
      console.error('Error fetching memberships:', error);
      showError('Erreur lors du chargement des abonnements');
      setMemberships([]);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/gym/clients/');
      
      let clientsData = [];
      if (Array.isArray(response.data)) {
        clientsData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        clientsData = response.data.results;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        clientsData = response.data.data;
      }
      
      setClients(clientsData);
      
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  };

  const filterMemberships = () => {
    console.log('Filtering memberships:', memberships);
    
    let filtered = Array.isArray(memberships) ? memberships : [];
    
    if (searchTerm) {
      filtered = filtered.filter(membership => {
        const clientName = getClientName(membership);
        const membershipType = membership.membership_type || 'N/A';
        const code = membership.code || 'N/A';
        
        return clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               membershipType.toLowerCase().includes(searchTerm.toLowerCase()) ||
               code.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(membership => {
        const status = getMembershipStatus(membership);
        return status === statusFilter;
      });
    }
    
    console.log('Filtered memberships:', filtered);
    setFilteredMemberships(filtered);
  };

  const getMembershipStatus = (membership) => {
    if (membership.status === 'CANCELLED') return 'CANCELLED';
    if (membership.status === 'SUSPENDED') return 'SUSPENDED';
    
    try {
      const today = new Date();
      const endDate = membership.end_date ? parseISO(membership.end_date) : null;
      
      if (!endDate || !isValid(endDate)) return 'ACTIVE';
      
      if (isBefore(endDate, today)) return 'EXPIRED';
      return 'ACTIVE';
    } catch (error) {
      console.error('Error getting membership status:', error);
      return 'ACTIVE';
    }
  };

  const getRemainingDays = (endDate) => {
    if (!endDate) return null;
    try {
      const end = parseISO(endDate);
      const today = new Date();
      if (!isValid(end)) return null;
      
      const days = differenceInDays(end, today);
      return days > 0 ? days : 0;
    } catch (error) {
      return null;
    }
  };

  const calculateEndDate = (startDate, durationMonths) => {
    try {
      const date = parseISO(startDate);
      if (!isValid(date)) {
        throw new Error('Date de début invalide');
      }
      const endDate = addMonths(date, parseInt(durationMonths));
      return format(endDate, 'yyyy-MM-dd');
    } catch (error) {
      console.error('Error calculating end date:', error);
      const defaultDate = addMonths(new Date(), parseInt(durationMonths));
      return format(defaultDate, 'yyyy-MM-dd');
    }
  };

  const handleOpenDialog = (membership = null) => {
    if (membership) {
      console.log('Editing membership:', membership);
      setEditingMembership(membership);
      
      // Calculer la durée en mois
      let durationMonths = 1;
      if (membership.start_date && membership.end_date) {
        try {
          const start = parseISO(membership.start_date);
          const end = parseISO(membership.end_date);
          if (isValid(start) && isValid(end)) {
            durationMonths = Math.round(differenceInDays(end, start) / 30);
          }
        } catch (error) {
          console.error('Error calculating duration:', error);
        }
      }
      
      setFormData({
        client: membership.client?.id || membership.client_id || membership.client || '',
        membership_type: membership.membership_type || 'MONTHLY',
        start_date: membership.start_date ? format(parseISO(membership.start_date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        duration_months: durationMonths,
        price_htg: membership.price_htg || '',
        price_usd: membership.price_usd || '',
        status: membership.status || 'ACTIVE',
        notes: membership.notes || '',
        end_date: membership.end_date || calculateEndDate(membership.start_date || new Date(), durationMonths)
      });
    } else {
      const endDate = calculateEndDate(new Date().toISOString().split('T')[0], 1);
      setEditingMembership(null);
      
      // Remplir avec les prix par défaut basés sur les paramètres du gym
      const defaultPriceUSD = membershipTypes.find(t => t.value === 'MONTHLY')?.base_price_usd || 30;
      const defaultPriceHTG = gymSettings 
        ? (defaultPriceUSD * (gymSettings.exchange_rate_usd_to_htg || 130)).toFixed(2)
        : (defaultPriceUSD * 130).toFixed(2);
      
      setFormData({
        client: '',
        membership_type: 'MONTHLY',
        start_date: format(new Date(), 'yyyy-MM-dd'),
        duration_months: 1,
        price_htg: defaultPriceHTG,
        price_usd: defaultPriceUSD.toString(),
        status: 'ACTIVE',
        notes: '',
        end_date: endDate
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMembership(null);
    setSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Valider les champs obligatoires
    if (!formData.client) {
      showError('Veuillez sélectionner un client');
      setSubmitting(false);
      return;
    }
    
    if (!formData.membership_type) {
      showError('Veuillez sélectionner un type d\'abonnement');
      setSubmitting(false);
      return;
    }
    
    if (!formData.price_htg) {
      showError('Veuillez renseigner le prix HTG');
      setSubmitting(false);
      return;
    }
    
    // Calculer la date de fin
    const endDate = calculateEndDate(formData.start_date, formData.duration_months);
    
    // Préparer les données pour l'API
    const submitData = {
      client: formData.client,
      membership_type: formData.membership_type,
      start_date: formData.start_date,
      end_date: endDate,
      price_htg: parseFloat(formData.price_htg) || 0,
      price_usd: parseFloat(formData.price_usd) || 0,
      status: formData.status,
      notes: formData.notes || ''
    };

    console.log('Sending data to API:', submitData);

    try {
      if (editingMembership) {
        await api.put(`/gym/memberships/${editingMembership.id}/`, submitData);
        showSuccess('Abonnement modifié avec succès');
      } else {
        await api.post('/gym/memberships/', submitData);
        showSuccess('Abonnement créé avec succès');
      }
      fetchMemberships();
      handleCloseDialog();
    } catch (error) {
      console.error('Submit error:', error.response?.data);
      showError('Erreur lors de l\'opération', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet abonnement?')) {
      try {
        await api.delete(`/gym/memberships/${id}/`);
        showSuccess('Abonnement supprimé avec succès');
        fetchMemberships();
      } catch (error) {
        showError('Erreur lors de la suppression');
      }
    }
  };

  const handleMembershipTypeChange = (type) => {
    const selectedType = membershipTypes.find(t => t.value === type);
    if (selectedType) {
      const endDate = calculateEndDate(formData.start_date, formData.duration_months);
      const basePriceUSD = selectedType.base_price_usd * formData.duration_months;
      const exchangeRate = gymSettings?.exchange_rate_usd_to_htg || 130;
      const calculatedPriceHTG = (basePriceUSD * exchangeRate).toFixed(2);
      
      setFormData({
        ...formData,
        membership_type: type,
        price_usd: basePriceUSD.toString(),
        price_htg: calculatedPriceHTG,
        end_date: endDate
      });
    }
  };

  const handleDurationChange = (duration) => {
    const newDuration = parseInt(duration) || 1;
    const endDate = calculateEndDate(formData.start_date, newDuration);
    const selectedType = membershipTypes.find(t => t.value === formData.membership_type);
    const basePriceUSD = selectedType ? selectedType.base_price_usd * newDuration : 0;
    const exchangeRate = gymSettings?.exchange_rate_usd_to_htg || 130;
    const calculatedPriceHTG = (basePriceUSD * exchangeRate).toFixed(2);
    
    setFormData({
      ...formData,
      duration_months: newDuration,
      price_usd: basePriceUSD.toString(),
      price_htg: calculatedPriceHTG,
      end_date: endDate
    });
  };

  const handlePriceChange = (field, value) => {
    const numericValue = parseFloat(value) || 0;
    
    if (field === 'price_htg') {
      setFormData({
        ...formData,
        price_htg: value,
        price_usd: (numericValue / (gymSettings?.exchange_rate_usd_to_htg || 130)).toFixed(2)
      });
    } else {
      setFormData({
        ...formData,
        price_usd: value,
        price_htg: (numericValue * (gymSettings?.exchange_rate_usd_to_htg || 130)).toFixed(2)
      });
    }
  };

  const handleStartDateChange = (date) => {
    const endDate = calculateEndDate(date, formData.duration_months);
    setFormData({
      ...formData,
      start_date: date,
      end_date: endDate
    });
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    
    try {
      let date;
      if (typeof dateInput === 'string') {
        date = dateInput.includes('T') ? parseISO(dateInput) : new Date(dateInput);
      } else if (dateInput instanceof Date) {
        date = dateInput;
      } else {
        date = new Date(dateInput);
      }
      
      if (!isValid(date)) {
        return 'Date invalide';
      }
      
      return format(date, 'dd/MM/yyyy', { locale: fr });
    } catch (error) {
      console.error('Date formatting error:', error, 'Input:', dateInput);
      return String(dateInput);
    }
  };

  const getStatusColor = (status) => {
    const found = statusOptions.find(s => s.value === status);
    return found ? found.color : 'default';
  };

  const getStatusLabel = (status) => {
    const found = statusOptions.find(s => s.value === status);
    return found ? found.label : status;
  };

  const getMembershipTypeLabel = (type) => {
    const found = membershipTypes.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getClientName = (membership) => {
    if (membership.client_name) return membership.client_name;
    if (membership.client_details?.first_name) {
      return `${membership.client_details.first_name} ${membership.client_details.last_name || ''}`;
    }
    if (membership.client?.first_name) {
      return `${membership.client.first_name} ${membership.client.last_name || ''}`;
    }
    
    // Rechercher le client dans la liste
    const client = Array.isArray(clients) ? clients.find(c => c.id === membership.client || c.id === membership.client_id) : null;
    if (client) {
      return `${client.first_name} ${client.last_name}`;
    }
    
    return 'Client inconnu';
  };

  const formatCurrency = (amount, currency = 'HTG') => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const showSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
  };

  const showError = (message, error = null) => {
    let errorMessage = message;
    if (error?.response?.data) {
      const errors = Object.values(error.response.data).flat();
      errorMessage = errors.join(', ') || message;
    }
    setSnackbar({ open: true, message: errorMessage, severity: 'error' });
  };

  // Calcul des statistiques
  const stats = {
    total: memberships.length,
    active: memberships.filter(m => getMembershipStatus(m) === 'ACTIVE').length,
    expired: memberships.filter(m => getMembershipStatus(m) === 'EXPIRED').length,
    monthlyRevenueHTG: memberships
      .filter(m => getMembershipStatus(m) === 'ACTIVE')
      .reduce((sum, m) => sum + (parseFloat(m.price_htg) || 0), 0),
    monthlyRevenueUSD: memberships
      .filter(m => getMembershipStatus(m) === 'ACTIVE')
      .reduce((sum, m) => sum + (parseFloat(m.price_usd) || 0), 0)
  };

  return (
    <Box p={3}>
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Receipt sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">📋 Abonnements du GYM</Typography>
            <Typography variant="body2" color="textSecondary">
              Gestion multi-devises des abonnements
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={loadingSettings}
        >
          Nouvel Abonnement
        </Button>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp color="primary" />
                <Typography variant="h6">Total</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircle />
                <Typography variant="h6">Actifs</Typography>
              </Box>
              <Typography variant="h4">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <TimerOff />
                <Typography variant="h6">Expirés</Typography>
              </Box>
              <Typography variant="h4">
                {stats.expired}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoney />
                <Typography variant="h6">Revenu HTG</Typography>
              </Box>
              <Typography variant="h4">
                {formatCurrency(stats.monthlyRevenueHTG, 'HTG')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={2.4}>
          <Card sx={{ bgcolor: 'secondary.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Euro />
                <Typography variant="h6">Revenu USD</Typography>
              </Box>
              <Typography variant="h4">
                {formatCurrency(stats.monthlyRevenueUSD, 'USD')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Rechercher client, type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                {statusOptions.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <ToggleButtonGroup
              value={currencyView}
              exclusive
              onChange={(e, value) => value && setCurrencyView(value)}
              size="small"
              fullWidth
            >
              <ToggleButton value="htg" color="primary">
                HTG
              </ToggleButton>
              <ToggleButton value="usd" color="primary">
                USD
              </ToggleButton>
              <ToggleButton value="both" color="primary">
                Les deux
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Actualiser'}
            </Button>
          </Grid>
          {gymSettings && (
            <Grid item xs={12} md={2}>
              <Chip
                icon={<CurrencyExchange />}
                label={`${gymSettings.primary_currency} (1 USD = ${gymSettings.exchange_rate_usd_to_htg} HTG)`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Client</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Période</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Prix</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Jours restants</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Chargement des abonnements...</Typography>
                </TableCell>
              </TableRow>
            ) : !Array.isArray(filteredMemberships) || filteredMemberships.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Box>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Aucun abonnement trouvé
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {memberships.length === 0 
                        ? 'Cliquez sur "Nouvel Abonnement" pour créer votre premier abonnement.'
                        : 'Aucun abonnement ne correspond aux filtres actuels.'}
                    </Typography>
                    {memberships.length === 0 && (
                      <Button 
                        variant="contained" 
                        sx={{ mt: 2 }}
                        onClick={() => handleOpenDialog()}
                      >
                        Créer un abonnement
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredMemberships.map((membership) => {
                if (!membership) return null;
                
                const status = getMembershipStatus(membership);
                const clientName = getClientName(membership);
                const remainingDays = getRemainingDays(membership.end_date);
                
                return (
                  <TableRow key={membership.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Person color="action" />
                        <Box>
                          <Typography fontWeight="bold">
                            {clientName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {membership.code || `ID: ${membership.id}`}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getMembershipTypeLabel(membership.membership_type)} 
                        color="primary" 
                        size="small" 
                      />
                      <Typography variant="caption" display="block" color="textSecondary">
                        {membership.duration_months || 1} mois
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CalendarToday fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatDate(membership.start_date)}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CalendarToday fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatDate(membership.end_date)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {currencyView === 'htg' || currencyView === 'both' ? (
                        <Box display="flex" alignItems="center" gap={0.5} mb={currencyView === 'both' ? 0.5 : 0}>
                          <Typography fontWeight="bold">
                            {formatCurrency(membership.price_htg, 'HTG')}
                          </Typography>
                        </Box>
                      ) : null}
                      {currencyView === 'usd' || currencyView === 'both' ? (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography 
                            fontWeight={currencyView === 'usd' ? 'bold' : 'normal'} 
                            color={currencyView === 'both' ? 'text.secondary' : 'inherit'}
                          >
                            {formatCurrency(membership.price_usd, 'USD')}
                          </Typography>
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(status)}
                        color={getStatusColor(status)}
                        size="small"
                        icon={status === 'ACTIVE' ? <CheckCircle /> : <Cancel />}
                      />
                    </TableCell>
                    <TableCell>
                      {remainingDays !== null && status === 'ACTIVE' ? (
                        <Badge
                          badgeContent={remainingDays}
                          color={remainingDays <= 7 ? 'error' : remainingDays <= 30 ? 'warning' : 'success'}
                          sx={{ '& .MuiBadge-badge': { fontSize: '0.8rem', height: '24px', minWidth: '24px' } }}
                        >
                          <AccessTime />
                        </Badge>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Modifier">
                          <IconButton 
                            color="primary" 
                            size="small"
                            onClick={() => handleOpenDialog(membership)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton 
                            color="error" 
                            size="small"
                            onClick={() => handleDelete(membership.id)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pour créer/modifier */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMembership ? '✏️ Modifier Abonnement' : '➕ Nouvel Abonnement'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Client *</InputLabel>
                  <Select
                    value={formData.client}
                    label="Client *"
                    onChange={(e) => setFormData({...formData, client: e.target.value})}
                    error={!formData.client}
                  >
                    <MenuItem value="">
                      <em>Sélectionnez un client</em>
                    </MenuItem>
                    {Array.isArray(clients) && clients
                      .filter(c => c?.is_active !== false)
                      .map(client => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.first_name} {client.last_name} ({client.code || client.id})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Type d'abonnement *</InputLabel>
                  <Select
                    value={formData.membership_type}
                    label="Type d'abonnement *"
                    onChange={(e) => handleMembershipTypeChange(e.target.value)}
                    error={!formData.membership_type}
                  >
                    {membershipTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label} - {formatCurrency(type.base_price_usd, 'USD')}/mois
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Durée (mois) *"
                  type="number"
                  value={formData.duration_months}
                  onChange={(e) => handleDurationChange(e.target.value)}
                  required
                  margin="normal"
                  inputProps={{ min: 1, max: 36 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Date de début *"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  required
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prix HTG *"
                  type="number"
                  value={formData.price_htg}
                  onChange={(e) => handlePriceChange('price_htg', e.target.value)}
                  required
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">HTG</InputAdornment>,
                  }}
                  inputProps={{ min: "0.01", step: "0.01" }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prix USD"
                  type="number"
                  value={formData.price_usd}
                  onChange={(e) => handlePriceChange('price_usd', e.target.value)}
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: "0", step: "0.01" }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date de fin"
                  type="date"
                  value={formData.end_date}
                  InputProps={{ readOnly: true }}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                  helperText="Calculée automatiquement"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.status}
                    label="Statut"
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    {statusOptions.map(status => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  multiline
                  rows={2}
                  margin="normal"
                  placeholder="Notes supplémentaires..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    Période: Du {formatDate(formData.start_date)} au {formatDate(formData.end_date)}
                    {' '}({formData.duration_months} mois)
                  </Typography>
                  <Typography variant="body2">
                    Prix: {formatCurrency(formData.price_htg, 'HTG')} / {formatCurrency(formData.price_usd, 'USD')}
                  </Typography>
                  <Typography variant="body2">
                    Statut: {getStatusLabel(formData.status)}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={submitting || !formData.client || !formData.membership_type || !formData.price_htg}
            >
              {submitting ? <CircularProgress size={24} /> : editingMembership ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GymMemberships;